/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: FirewallServicesPage.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    Firewall Services page - Lists and manages firewall services
----------------------------------------------------------------*/

import { useState, useCallback } from 'react';
import { Shield } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useFirewallServicesList } from './hooks/useFirewallServicesList';
import { FirewallServiceModal } from './components/FirewallServiceModal';
import { deleteFirewallService } from './api/firewallServicesApi';
import { Edit, Trash2 } from 'lucide-react';
import type { FirewallService } from './types';

export default function FirewallServicesPage() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<FirewallService | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    service: FirewallService | null;
  }>({
    isOpen: false,
    service: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    services,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useFirewallServicesList();

  const handleAdd = useCallback(() => {
    setEditingService(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((service: FirewallService) => {
    setEditingService(service);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback((service: FirewallService) => {
    setDeleteModal({ isOpen: true, service });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.service) return;

    setIsDeleting(true);
    try {
      await deleteFirewallService(deleteModal.service.id);
      showSuccess(t('firewallServices.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, service: null });
      refresh();
    } catch (error) {
      showError(error.message || t('firewallServices.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.service, refresh, showSuccess, showError, t]);

  const handleModalSuccess = useCallback(() => {
    refresh();
  }, [refresh]);

  const renderActions = useCallback(
    (service: FirewallService) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            onClick={() => handleEdit(service)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            onClick={() => handleDelete(service)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleEdit, handleDelete, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          title={t('firewallServices.title')}
          titleIcon={<Shield className="h-5 w-5 text-primary" />}
          data={services}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onSearch={handleSearch}
          onColumnToggle={handleColumnToggle}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          emptyMessage={t('firewallServices.noServicesFound')}
          searchPlaceholder={t('firewallServices.searchPlaceholder')}
          renderActions={renderActions}
          addButtonText={t('common.add')}
          onAdd={handleAdd}
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll'),
          }}
        />
      }
      formView={null}
    >
      <FirewallServiceModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingService(null);
        }}
        onSuccess={handleModalSuccess}
        service={editingService}
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, service: null })}
        onConfirm={handleDeleteConfirm}
        title={t('firewallServices.deleteModal.title')}
        message={t('firewallServices.deleteModal.message', {
          name: deleteModal.service?.name || '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
